import React from "react";

/** Shared ePSF pill badges — same value → same colour everywhere. */

const CONDITION_META = {
  acceptable: { tone: "condition-acceptable", label: "Acceptable" },
  damaged: { tone: "condition-damaged", label: "Damaged" },
  temperatureconcern: {
    tone: "condition-temp",
    label: "Temperature concern",
  },
  packagingissue: { tone: "condition-packaging", label: "Packaging issue" },
  quantitydiscrepancy: {
    tone: "condition-qty",
    label: "Quantity discrepancy",
  },
  other: { tone: "condition-other", label: "Other" },
};

const RECEIPT_STATUS_META = {
  posted: { tone: "status-posted", label: "Posted" },
  requiresreview: { tone: "status-review", label: "Requires review" },
  hold: { tone: "status-hold", label: "Hold" },
  quarantined: { tone: "status-quarantine", label: "Quarantined" },
  released: { tone: "status-released", label: "Released" },
  returnpending: { tone: "status-review", label: "Return pending" },
  destructionpending: {
    tone: "status-quarantine",
    label: "In destruction",
  },
  quarantine: { tone: "status-quarantine", label: "Quarantined" },
  dispenserequested: { tone: "status-review", label: "Dispense requested" },
  destroyrequested: { tone: "status-quarantine", label: "Destruction requested" },
  returnrequested: { tone: "status-review", label: "Return requested" },
  stockreturnrequested: { tone: "status-review", label: "Stock return requested" },
  requested: { tone: "status-review", label: "Requested" },
  available: { tone: "status-posted", label: "Available" },
  returned: { tone: "status-default", label: "Returned" },
  returnedexternal: { tone: "status-default", label: "Returned external" },
  dispensed: { tone: "status-released", label: "Dispensed" },
  destroyed: { tone: "status-quarantine", label: "Destroyed" },
};

function normalizeKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

function humanizeCode(value) {
  return String(value || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function humanizeEpsfCode(value) {
  return humanizeCode(value);
}

const REVIEW_REASON_LABELS = {
  storagecompatibilityoverride: "Storage compatibility override",
  temperatureoutofrange: "Temperature out of range",
  conditionnotacceptable: "Condition not acceptable",
  temperaturerange: "Temperature range",
};

export function formatReviewReasonLabel(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const key = normalizeKey(raw);
  return REVIEW_REASON_LABELS[key] || humanizeCode(raw);
}

export function formatConditionLabel(value) {
  const key = normalizeKey(value);
  if (!key) return "";
  return CONDITION_META[key]?.label || humanizeCode(value);
}

export function formatReceiptStatusLabel(value) {
  const key = normalizeKey(value);
  if (!key) return "";
  return RECEIPT_STATUS_META[key]?.label || humanizeCode(value);
}

function renderPill(tone, label) {
  if (!label) return "—";
  return (
    <span className={`epsf-imp-pill epsf-imp-pill--${tone}`} title={label}>
      {label}
    </span>
  );
}

export function renderConditionBadge(value) {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  const meta = CONDITION_META[normalizeKey(raw)];
  return renderPill(meta?.tone || "condition-other", meta?.label || humanizeCode(raw));
}

export function renderReceiptStatusBadge(value) {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  const meta = RECEIPT_STATUS_META[normalizeKey(raw)];
  return renderPill(meta?.tone || "status-default", meta?.label || humanizeCode(raw));
}

const QUEUE_STATUS_META = {
  waitingforfill: { tone: "status-released", label: "Waiting for fill" },
  filloverdue: { tone: "status-quarantine", label: "Fill overdue" },
  waitingforapproval: { tone: "status-review", label: "Waiting for approval" },
  approvaloverdue: { tone: "status-quarantine", label: "Approval overdue" },
  waitingforcorrection: { tone: "status-hold", label: "Waiting for correction" },
  correctionoverdue: { tone: "status-quarantine", label: "Correction overdue" },
  approved: { tone: "status-posted", label: "Approved" },
};

const TRIGGER_LABELS = {
  onreceipt: "On receipt",
  receiptposted: "On receipt",
  ondispense: "On dispense",
  dispenseposted: "On dispense",
  onparticipantreturn: "On participant return",
  participantreturnrequested: "On participant return",
  ondestroy: "On destruction",
  destroyposted: "On destruction",
  destructionrequested: "Destruction requested",
  destroyrequested: "Destruction requested",
  onreconcile: "On reconcile",
  reconcileposted: "On reconcile",
  recurringtemp: "Temperature check",
  recurring: "Recurring",
  manual: "Manual",
};

export function formatFormTriggerLabel(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return TRIGGER_LABELS[normalizeKey(raw)] || humanizeCode(raw);
}

export function renderQueueStatusBadge(value) {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  const meta = QUEUE_STATUS_META[normalizeKey(raw)];
  return renderPill(
    meta?.tone || "status-default",
    meta?.label || humanizeCode(raw),
  );
}
